import React, { useState, useEffect, useContext, useRef, useCallback } from "react"

import { Button, Form } from "react-bootstrap"
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';

import CourseTree from "./components/tree/CourseTree"
import SelectCourse from "./components/course/SelectCourse"
import EditCourse from "./components/course/EditCourse"
import NewCourse from "./components/course/NewCourse"
import LinkedCourses from "./components/course/LinkedCourses"
import TopicAudioManager from "./components/topic/TopicAudioManager"
import CourseDurationModal from "./components/duration/CourseDurationModal"

import ImageUploader from "./ImageUploader"
import CardPreview from './CardPreview'
import ContentReviewPanel from './components/review/ContentReviewPanel'
import CourseReviewModal from './components/review/CourseReviewModal'
import { nextCardWithFindings, otherCardsWithFindings } from './components/review/reviewRuns'
import { getCourseOpenFindings } from './api/reviews'
import FactsPanel from './components/facts/FactsPanel'
import axios from 'axios'
import { applyFix, applyFixes } from './services/fixDiff'
import { MARK_STYLES, clearMarks, insertGhost, markText } from './services/editorMarks'
import { plainText } from './services/findingText'
import { loadLastEdited, saveLastEdited } from './services/editorSession'
import { UserContext } from './hooks/UserContext';
import { ContentContext } from "./hooks/ContentContext";

import 'tinymce/tinymce'
import 'tinymce/icons/default'
import 'tinymce/themes/silver'
import 'tinymce/skins/ui/oxide/skin.min.css'
import 'tinymce/skins/ui/oxide/content.min.css'
import 'tinymce/skins/content/default/content.min.css'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/image'
import 'tinymce/plugins/link'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/nonbreaking'
import 'tinymce/plugins/paste'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/code'
import { Editor } from "@tinymce/tinymce-react"
import Header from './components/Header'

import './css/CardEditor.css';

// The toolbar's Check modes (CONTEXT.md "Check").
const CHECK_MODES = [
  { mode: 'proofread', label: 'Proofread', title: 'Spelling, grammar and punctuation. About 20 s.' },
  { mode: 'full', label: 'Full review', title: 'Proofread, plus facts, outdated info, style and suggestions. About 2 min.' },
];

// The selection to reopen: a card linked from elsewhere
// (?course=…&card=…), else the last-edited topic/card.
function initialRestore(search) {
  const params = new URLSearchParams(search || '');
  const courseId = Number(params.get('course'));
  const cardId = Number(params.get('card'));
  return courseId && cardId ? { courseId, cardId } : loadLastEdited();
}

// Review marks in the editor body (services/editorMarks): fixes applied
// this session, and the review panel's active finding with an addition's
// ghost sentence. Returns the active finding's mark spans.
function drawReviewMarks(editor, appliedTexts, active) {
  clearMarks(editor, ['active', 'ghost', 'applied']);
  appliedTexts.forEach(text => markText(editor, text, 'applied'));
  if (!active) return [];
  const spans = markText(editor, active.text, 'active', active.occurrence);
  if (spans.length && active.ghost) insertGhost(spans[spans.length - 1], active.ghost);
  return spans;
}

export default function CardEditor({ location, props }) {
  const [content, setContent] = useState("");
  const [selectedCourse, setSelectedCourse] = useState({});
  const [selectedTopic, setSelectedTopic] = useState({});
  const [selectedCard, setSelectedCard] = useState();
  const [isAudioManagerVisible, setAudioManagerVisible] = useState(false);
  const [isDurationModalVisible, setDurationModalVisible] = useState(false);
  const [rightPanel, setRightPanel] = useState('preview'); // 'preview' | 'review'
  // The open card's latest check: its mode ('proofread' | 'full') and
  // whether it is running.
  const [checkStatus, setCheckStatus] = useState({ mode: null, running: false });
  const reviewPanelRef = useRef(null);
  // Fixes applied from the review panel stay marked in the editor until
  // the card changes or a new check starts; the panel's active finding is
  // marked too. Both are redrawn whenever TinyMCE sets its content.
  const appliedTextsRef = useRef([]);
  const activeFindingRef = useRef(null);
  // The current selection, read by handlers the tree calls from stale closures.
  const selectionRef = useRef({});
  selectionRef.current = { topicId: selectedTopic?.id, cardId: selectedCard?.id };
  // The course's cards in reading order with the open findings of their
  // latest check: the sidebar's counts and the review panel's next card.
  const [openFindings, setOpenFindings] = useState([]);
  const [courseCheckVisible, setCourseCheckVisible] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('nanoms-tree-width');
    return saved ? parseInt(saved, 10) : 300;
  });
  const isResizing = useRef(false);
  const selectCourseRef = useRef(null);
  // Topic/card to reopen (a linked card, else the last edited); consumed on
  // the first course selection (SelectCourse restores the course itself).
  const pendingRestoreRef = useRef(initialRestore(location?.search));
  const restoringRef = useRef(false);

  const { user } = useContext(UserContext);
  const toast = useRef(null);
  const editorRef = useRef(null);

  // Auto-save: 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
  const [saveStatus, setSaveStatus] = useState('idle');
  const dirtyRef = useRef(false);
  const contentRef = useRef("");
  const positionRef = useRef(null);
  const savedCardIdRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const savedTimerRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const savePromiseRef = useRef(null);
  const AUTOSAVE_DELAY_MS = 1500;

  const handleMouseDown = (e) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      const newWidth = Math.min(Math.max(startWidth + (e.clientX - startX), 220), 600);
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setSidebarWidth(w => { localStorage.setItem('nanoms-tree-width', w); return w; });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    const getContent = async () => {
      if (selectedCard) {
        const card = await getCard(selectedCard.id);
        setContent(card.body);
        contentRef.current = card.body;
        positionRef.current = card.position_id;
        savedCardIdRef.current = selectedCard.id;
        appliedTextsRef.current = [];
      } else {
        setContent("");
        contentRef.current = "";
        positionRef.current = null;
        savedCardIdRef.current = null;
      }
      dirtyRef.current = false;
      setSaveStatus('idle');
    }
    getContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard?.id]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleEditorChange = (content, editor) => {
    setContent(content);
    contentRef.current = content;
    // Only mark dirty for edits to the currently-loaded card; ignore the
    // initial onChange that fires while the load useEffect is populating value.
    if (savedCardIdRef.current && savedCardIdRef.current === selectedCard?.id) {
      markDirty();
    }
  }

  const getCard = async (cardId) => {
    let data = [];
    try {
      const res = await axios.get(`/api/cards/${cardId}`, {
        headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
      });
      data = res.data;
    } catch (error) {
      console.error(error);
    }
    return data
  }

  const getTopic = async (topicId) => {
    let data = [];
    try {
      const res = await axios.get(`/api/topics/${topicId}`, {
        headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
      });
      data = res.data;
    } catch (error) {
      console.error(error);
    }
    return data
  }

  const updateContent = async (cardId, cardContent, positionId) => {
    let data = [];
    let res;
    try {
      res = await axios.put(`/api/cards/${cardId}`, {
        // body HTML is normalized server-side (backend html_normalizer)
        body: cardContent,
        position_id: positionId
      }, {
        headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
      });
      data = res.data;
      console.log(data);
    } catch (error) {
      console.error(error);
    }
    return res;
  }

  const markDirty = () => {
    dirtyRef.current = true;
    setSaveStatus('dirty');
    scheduleAutosave();
  };

  const scheduleAutosave = () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      flushSave();
    }, AUTOSAVE_DELAY_MS);
  };

  // Saves now if dirty. Returns true if save succeeded (or nothing to save).
  const flushSave = async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (saveInFlightRef.current && savePromiseRef.current) {
      const inFlightOk = await savePromiseRef.current;
      if (!inFlightOk) return false;
      if (!dirtyRef.current) return true;
    }
    if (!dirtyRef.current) return true;
    const cardId = savedCardIdRef.current;
    if (!cardId) return true;
    const contentSnapshot = contentRef.current;
    const positionSnapshot = positionRef.current;
    setSaveStatus('saving');
    saveInFlightRef.current = true;
    savePromiseRef.current = updateContent(cardId, contentSnapshot, positionSnapshot)
      .then(res => {
        if (res && res.status === 200) {
          const isLatestSnapshot =
            savedCardIdRef.current === cardId &&
            contentRef.current === contentSnapshot &&
            positionRef.current === positionSnapshot;
          if (isLatestSnapshot) {
            dirtyRef.current = false;
            setSaveStatus('saved');
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(() => {
              savedTimerRef.current = null;
              setSaveStatus(prev => (prev === 'saved' ? 'idle' : prev));
            }, 2000);
          } else {
            dirtyRef.current = true;
            setSaveStatus('dirty');
            scheduleAutosave();
          }
          return true;
        }
        setSaveStatus('error');
        return false;
      })
      .finally(() => {
        saveInFlightRef.current = false;
        savePromiseRef.current = null;
      });

    const ok = await savePromiseRef.current;
    if (!ok) return false;
    if (dirtyRef.current) return flushSave();
    return true;
  };

  const handlePositionDisplay = () => {
    return selectedCard['position_id'];
  }

  const handlePublishClick = async (event) => {
    // Force a save even if not dirty, to preserve existing manual-save semantics.
    dirtyRef.current = true;
    const ok = await flushSave();
    if (ok) {
      toast.current.show({ severity: 'success', summary: 'Card updated', detail: 'Content was properly saved', life: 3000 });
    } else {
      toast.current.show({ severity: 'warn', summary: 'Possible problem', detail: 'Card might not be properly saved. Please check content.', life: 5000 });
    }
  }

  // Flush any pending edits before navigating away. If the save fails,
  // confirm with the user before discarding their work.
  const guardNavigation = async () => {
    if (!dirtyRef.current) return true;
    const ok = await flushSave();
    if (ok) return true;
    return window.confirm(
      "Couldn't save your changes. Leave anyway and lose unsaved edits?"
    );
  };

  const handleCourseChange = async (currentCourse) => {
    if (!(await guardNavigation())) return false;
    setSelectedCourse(currentCourse);
    setSelectedTopic({});
    setSelectedCard(null);
    restoreLastSelection(currentCourse);
    return true;
  };

  const restoreLastSelection = async (currentCourse) => {
    const pending = pendingRestoreRef.current;
    pendingRestoreRef.current = null;
    if (!pending || pending.courseId !== currentCourse.id) return;
    restoringRef.current = true;
    try {
      await restoreSelection(pending, currentCourse);
    } finally {
      restoringRef.current = false;
    }
  };

  const restoreSelection = async (pending, currentCourse) => {
    // A linked card comes without its topic: read it from the card.
    const topicId = pending.topicId || (pending.cardId && (await getCard(pending.cardId))?.topic_id);
    if (!topicId) return;

    const topic = await getTopic(topicId);
    if (!topic?.id || topic.course_id !== currentCourse.id) return; // deleted or moved

    if (pending.cardId) {
      const card = await getCard(pending.cardId);
      if (card?.id && card.topic_id === topic.id) {
        setSelectedTopic(topic);
        setSelectedCard(card);
        return;
      }
    }
    setSelectedTopic(topic);
  };

  // Remember the current selection so the next session reopens it
  useEffect(() => {
    if (!selectedCourse.id) return;
    saveLastEdited({
      courseId: selectedCourse.id,
      topicId: selectedTopic?.id,
      cardId: selectedCard?.id,
    });
  }, [selectedCourse.id, selectedTopic?.id, selectedCard?.id]);

  // The tree picks a course's first card by itself (`auto`); that pick
  // yields to a card being restored or already chosen.
  const yieldsToSelection = (auto) => auto && (
    restoringRef.current || selectionRef.current.topicId || selectionRef.current.cardId
  );

  const handleTopicChange = async (currentTopicId, { auto = false } = {}) => {
    if (yieldsToSelection(auto)) return false;
    if (!(await guardNavigation())) return false;
    const currentTopic = await getTopic(currentTopicId);
    setSelectedTopic(currentTopic);
    setSelectedCard(null);
    return true;
  };

  const handleCardChange = async (currentCardId, currentTopicId, { auto = false } = {}) => {
    if (yieldsToSelection(auto)) return false;
    if (!(await guardNavigation())) return false;
    const card = await getCard(currentCardId);
    const topic = await getTopic(currentTopicId);
    setSelectedCard(card);
    setSelectedTopic(topic);
    return true;
  };

  const handleTopicFieldChange = (field, value) => {
    setSelectedTopic(prev => ({ ...prev, [field]: value }));
  };

  const updateTopic = async () => {
    try {
      const res = await axios.put(`/api/topics/${selectedTopic.id}`, {
        name: selectedTopic.name,
        type: selectedTopic.type,
        position_id: selectedTopic.position_id,
      }, {
        headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
      });
      toast.current.show({ severity: 'success', summary: 'Topic updated', detail: `Topic "${res.data.name}" saved`, life: 3000 });
    } catch (error) {
      console.error(error);
      toast.current.show({ severity: 'warn', summary: 'Error', detail: 'Topic might not be properly saved.', life: 5000 });
    }
  };

  // Breadcrumb path
  const breadcrumbParts = [selectedCourse.name || 'Course'];
  if (selectedTopic && selectedTopic.name) breadcrumbParts.push(selectedTopic.name);
  if (selectedCard) breadcrumbParts.push(`Card ${selectedCard.id}`);

  const handleReviewStatus = useCallback((status) => {
    setCheckStatus(status);
    // A check starts: the marks of fixes applied from the last one go.
    if (status.running) {
      appliedTextsRef.current = [];
      if (editorRef.current?.getBody?.()) clearMarks(editorRef.current, ['applied']);
    }
  }, []);

  // Check in the toolbar: open the review panel on that check, showing the
  // card's latest one of that mode or offering to start it.
  const handleCheck = (mode) => {
    if (!selectedCard || checkStatus.running) return;
    setRightPanel('review');
    reviewPanelRef.current?.check(mode);
  };

  const handleFactsChange = useCallback((updatedFacts) => {
    setSelectedCard(prev => prev ? { ...prev, facts: updatedFacts } : prev);
  }, []);

  const loadOpenFindings = useCallback(async (courseId) => {
    if (!courseId) {
      setOpenFindings([]);
      return;
    }
    try {
      setOpenFindings(await getCourseOpenFindings(user.token, courseId));
    } catch {
      setOpenFindings([]);
    }
  }, [user.token]);

  useEffect(() => {
    loadOpenFindings(selectedCourse.id);
  }, [selectedCourse.id, loadOpenFindings]);

  // The review panel keeps the open card's count current as findings are handled.
  const handleOpenCount = useCallback((cardId, open) => {
    setOpenFindings(prev => prev.map(c => (c.card_id === cardId ? { ...c, open } : c)));
  }, []);

  // Open a card of the course check in the editor, review panel showing.
  const openCheckedCard = async (cardId) => {
    const topicId = openFindings.find(c => c.card_id === cardId)?.topic_id ?? (await getCard(cardId))?.topic_id;
    if (!topicId) return;
    setRightPanel('review');
    await handleCardChange(cardId, topicId);
  };

  const closeCourseCheck = () => {
    setCourseCheckVisible(false);
    loadOpenFindings(selectedCourse.id);
    // The check may have started runs on the open card.
    reviewPanelRef.current?.reload();
  };

  const nextCard = nextCardWithFindings(openFindings, selectedCard?.id);

  // The review panel's active finding: highlight its text in the editor and
  // show an addition's sentence as ghost text after it.
  const handleShowFinding = useCallback((target) => {
    activeFindingRef.current = target;
    const editor = editorRef.current;
    if (!editor?.getBody?.()) return;
    const spans = drawReviewMarks(editor, appliedTextsRef.current, target);
    if (spans.length) spans[0].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  // Where the check's progress line sits: the top of TinyMCE's edit area,
  // right under its toolbar.
  const getProgressHost = useCallback(
    () => editorRef.current?.getContainer?.()?.querySelector('.tox-edit-area') || null,
    [],
  );

  // Apply a harmonized review fix: exact-match replace of anchorHtml in
  // the current editor content. Returns false when the anchor is stale
  // (body edited since review) so the panel can fall back to manual flow.
  const handleApplyFix = useCallback((anchorHtml, replacementHtml) => {
    const editor = editorRef.current;
    if (!editor) return false;
    const updated = applyFix(editor.getContent(), anchorHtml, replacementHtml);
    if (updated === null) return false;
    appliedTextsRef.current.push(plainText(replacementHtml));
    // Single undo step; setContent fires onEditorChange → dirty/autosave.
    editor.undoManager.transact(() => {
      editor.setContent(updated);
    });
    return true;
  }, []);

  // Apply all: every fix in one undo step. Returns the ids that applied;
  // a fix whose text has changed is skipped and stays in the panel.
  const handleApplyFixes = useCallback((fixes) => {
    const editor = editorRef.current;
    if (!editor) return [];
    const { html, appliedIds } = applyFixes(editor.getContent(), fixes);
    if (appliedIds.length) {
      fixes.filter(fix => appliedIds.includes(fix.id))
        .forEach(fix => appliedTextsRef.current.push(plainText(fix.replacementHtml)));
      editor.undoManager.transact(() => {
        editor.setContent(html);
      });
    }
    return appliedIds;
  }, []);

  const quizDialog = (editor, target = null) => {
    return editor.windowManager.open({
      title: 'Quiz Details',
      body: {
        type: 'panel',
        items: [
          { type: 'input', name: 'title', label: 'Title' },
          { type: 'textarea', name: 'question', label: 'Question' },
          { type: 'input', name: 'answer1', label: 'Answer 1' },
          { type: 'input', name: 'answer2', label: 'Answer 2' },
          { type: 'input', name: 'answer3', label: 'Answer 3' },
          {
            type: 'selectbox', name: 'correct_answer', label: 'Correct Answer', size: 1,
            items: [
              { value: '1', text: 'Answer 1' },
              { value: '2', text: 'Answer 2' },
              { value: '3', text: 'Answer 3' }
            ]
          }
        ]
      },
      buttons: [
        { type: 'cancel', text: 'Close' },
        { type: 'submit', text: 'Save', primary: true }
      ],
      initialData: {
        title: target ? target.getAttribute('title') : "",
        question: target ? target.getAttribute('question') : "",
        answer1: target ? target.getAttribute('answer1') : "",
        answer2: target ? target.getAttribute('answer2') : "",
        answer3: target ? target.getAttribute('answer3') : "",
        correct_answer: target ? target.getAttribute('correct_answer') : "",
      },
      onSubmit: function (api) {
        var data = api.getData();
        if (target) {
          target.setAttribute('title', data.title)
          target.setAttribute('question', data.question)
          target.setAttribute('answer1', data.answer1)
          target.setAttribute('answer2', data.answer2)
          target.setAttribute('answer3', data.answer3)
          target.setAttribute('correct_answer', data.correct_answer)
        } else {
          editor.insertContent('<quiz title="' + data.title + '"' +
            'question="' + data.question + '"' +
            'answer1="' + data.answer1 + '"' +
            'answer2="' + data.answer2 + '"' +
            'answer3="' + data.answer3 + '"' +
            'correct_answer="' + data.correct_answer + '"' +
            '"<div> Quiz block </div> </quiz>');
        }
        api.close();
      }
    });
  };

  const noteBlockDialog = (editor, target = null) => {
    return editor.windowManager.open({
      type: 'panel',
      title: 'Note Block Details',
      size: 'large',
      body: {
        type: 'panel',
        items: [
          {
            type: 'selectbox', name: 'template', label: 'Choose Template', size: 1,
            items: [
              { value: 'fact', text: 'Fun Fact' },
              { value: 'focus', text: 'Stay Focused' },
              { value: 'summary', text: 'Summary' },
              { value: 'didyouknow', text: 'Did you know?' },
              { value: 'nerdishness', text: 'A bit of Nerdish-ness' }
            ]
          },
          { type: 'textarea', name: 'text', label: 'Text', maximized: true },
        ]
      },
      buttons: [
        { type: 'cancel', text: 'Close' },
        { type: 'submit', text: target ? 'Save' : 'Add', primary: true }
      ],
      initialData: {
        template: target ? ({'Fun Fact': 'fact', 'Stay Focused': 'focus', 'Summary': 'summary', 'Did you know?': 'didyouknow', 'A bit of Nerdish-ness': 'nerdishness'}[target.getAttribute('title')] || '') : "",
        text: target ? target.textContent : "",
      },
      onSubmit: function (api) {
        var data = api.getData();
        var templates = [
          { key: 'fact', title: 'Fun Fact', image: 'https://nerdish-images.s3.eu-central-1.amazonaws.com/button-card-images/fun_fact.png' },
          { key: 'focus', title: 'Stay Focused', image: 'https://nerdish-images.s3.eu-central-1.amazonaws.com/button-card-images/stay_focused.png' },
          { key: 'summary', title: 'Summary', image: 'https://nerdish-images.s3.eu-central-1.amazonaws.com/button-card-images/summary.png' },
          { key: 'didyouknow', title: 'Did you know?', image: 'https://nerdish-images.s3.eu-central-1.amazonaws.com/button-card-images/summary.png' },
          { key: 'nerdishness', title: 'A bit of Nerdish-ness', image: 'https://nerdish-images.s3.eu-central-1.amazonaws.com/button-card-images/summary.png' },
        ];
        var selected_template = templates.find(t => t.key === data.template);
        if (!selected_template) {
          console.log(`Sorry, there is no ${data.template} template.`);
          return;
        }
        if (target) {
          target.setAttribute('img', selected_template.image);
          target.setAttribute('title', selected_template.title);
          target.textContent = data.text;
        } else {
          editor.insertContent('<note img="' + selected_template.image + '" ' +
            'title="' + selected_template.title + '">' +
            data.text +
            '</note>');
        }
        api.close();
      }
    });
  };

  const getCourses = async () => {
    try {
      let courses = [];
      let currentPage = 1, totalPages;
      do {
        const { data } = await axios.get(`/api/courses?page=${currentPage}&per_page=100`, {
          headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
        });
        courses = [...courses, ...data.items.map(d => ({ value: d.id.toString(), text: d.name }))];
        totalPages = data._meta.total_pages;
        currentPage++;
      } while (currentPage <= totalPages);
      return courses;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  const courseCardDialog = async (editor, target = null) => {
    const courseItems = await getCourses();
    return editor.windowManager.open({
      type: 'panel',
      title: 'Course Card Details',
      size: 'medium',
      body: {
        type: 'panel',
        items: [
          { type: 'selectbox', name: 'course', label: 'Choose Course', size: 1, items: courseItems },
          { type: 'input', name: 'titleText', label: 'Title' },
          { type: 'input', name: 'actionText', label: 'Text on button' },
          { type: 'input', name: 'postActionText', label: 'Text to show after button got clicked' }
        ]
      },
      buttons: [
        { type: 'cancel', text: 'Close' },
        { type: 'submit', text: 'Add', primary: true }
      ],
      initialData: {
        course: target ? target.getAttribute('id') : '',
        titleText: target ? target.getAttribute('title') : "You may be interested in",
        actionText: target ? target.getAttribute('actiontext') : "Add to My Topics",
        postActionText: target ? target.getAttribute('postactiontext') : "Topic added. Enjoy your reading!",
      },
      onSubmit: function (api) {
        var data = api.getData();
        if (target) {
          target.setAttribute('id', data.course)
          target.setAttribute('title', data.titleText)
          target.setAttribute('actiontext', data.actionText)
          target.setAttribute('postactiontext', data.postActionText)
          target.setAttribute('formaction', '#enroll/' + data.course)
        } else {
          editor.insertContent('<course id="' + data.course + '"' +
            'title="' + data.titleText + '"' +
            'actiontext="' + data.actionText + '"' +
            'formaction="#enroll/' + data.course + '"' +
            'postactiontext="' + data.postActionText + '"' +
            '>Course block</course>');
        }
        api.close();
      }
    });
  };

  const APPCARD_ATTRS = ['app', 'ios', 'android', 'name', 'tagline', 'rating', 'img', 'text'];

  const getPromotedApps = async () => {
    try {
      const { data } = await axios.get('/api/promoted-apps', {
        headers: { 'Authorization': `Bearer ${user.token}`, 'X-EDITOR': 'nanoms' }
      });
      return data.items;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // Returns an error string, or null when the app card data is valid.
  const validateAppCard = (data) => {
    const host = (url, expected) => {
      try {
        const u = new URL(url);
        return u.protocol === 'https:' && u.hostname === expected;
      } catch (e) {
        return false;
      }
    };
    if (!host(data.ios, 'apps.apple.com')) return 'iOS URL must be https://apps.apple.com/…';
    if (data.android && !host(data.android, 'play.google.com')) return 'Android URL must be https://play.google.com/…';
    if (!data.name.trim()) return 'Name is required';
    if (!/^https:\/\/.+/.test(data.img)) return 'Icon must be an https URL (upload it in the image panel and paste the link)';
    if (data.rating && !(/^\d(\.\d)?$/.test(data.rating) && Number(data.rating) <= 5)) return 'Rating must be 0–5 with at most one decimal';
    return null;
  };

  const appCardDialog = async (editor, target = null) => {
    const attr = (name, fallback = '') => (target && target.getAttribute(name)) || fallback;
    const apps = await getPromotedApps();
    return editor.windowManager.open({
      type: 'panel',
      title: 'App Card Details',
      size: 'medium',
      body: {
        type: 'panel',
        items: [
          {
            type: 'selectbox', name: 'app', label: 'App (fills the fields below)', size: 1,
            items: [{ value: '', text: '— custom —' }, ...apps.map(a => ({ value: a.slug, text: a.name }))]
          },
          { type: 'input', name: 'ios', label: 'App Store URL (required)' },
          { type: 'input', name: 'android', label: 'Google Play URL (optional — Android readers see no card without it)' },
          { type: 'input', name: 'name', label: 'App name' },
          { type: 'input', name: 'tagline', label: 'Tagline' },
          { type: 'input', name: 'img', label: 'Icon URL (upload in the image panel, paste the link)' },
          { type: 'input', name: 'rating', label: 'Rating 0–5 (optional, e.g. 4.8)' },
          { type: 'input', name: 'text', label: 'Button text' },
        ]
      },
      buttons: [
        { type: 'cancel', text: 'Close' },
        { type: 'submit', text: target ? 'Save' : 'Add', primary: true }
      ],
      initialData: {
        app: attr('app'), ios: attr('ios'), android: attr('android'), name: attr('name'),
        tagline: attr('tagline'), img: attr('img'), rating: attr('rating'),
        text: attr('text', 'Get'),
      },
      onChange: function (api, details) {
        if (details.name !== 'app') return;
        const preset = apps.find(a => a.slug === api.getData().app);
        if (!preset) return;
        api.setData({
          ios: preset.ios, android: preset.android || '', name: preset.name,
          tagline: preset.tagline || '', img: preset.img,
          rating: preset.rating == null ? '' : String(preset.rating),
        });
      },
      onSubmit: function (api) {
        const data = api.getData();
        APPCARD_ATTRS.forEach(k => { data[k] = (data[k] || '').trim(); });
        const error = validateAppCard(data);
        if (error) {
          editor.windowManager.alert(error);
          return;
        }
        // Attribute edits must emit change for React's preview and autosave,
        // and the whole dialog submission should be one undo step.
        editor.undoManager.transact(() => {
          if (target) {
            APPCARD_ATTRS.forEach(k => {
              if (data[k]) target.setAttribute(k, data[k]); else target.removeAttribute(k);
            });
          } else {
            // A click on an existing card leaves the caret inside it.
            // Insert after the outermost card, never inside its payload.
            const containingCard = editor.dom.getParents(editor.selection.getNode(), 'appcard').pop();
            if (containingCard) {
              const range = editor.dom.createRng();
              range.setStartAfter(containingCard);
              range.collapse(true);
              editor.selection.setRng(range);
            }
            const attrs = APPCARD_ATTRS
              .filter(k => data[k])
              .map(k => ` ${k}="${editor.dom.encode(data[k])}"`)
              .join('');
            editor.insertContent(`<appcard${attrs}></appcard>`);
          }
        });
        api.close();
      }
    });
  };

  const init_instance = (editor) => {
    editorRef.current = editor;
    // A new body (card load, Apply, undo) drops the review marks: redraw.
    editor.on('SetContent', () => {
      drawReviewMarks(editor, appliedTextsRef.current, activeFindingRef.current);
    });
    editor.on('click', function (e) {
      if (e.target.nodeName === "QUIZ") {
        quizDialog(this, e.target);
      }
      if (e.target.nodeName === "COURSE") {
        courseCardDialog(this, e.target);
      }
      if (e.target.nodeName === "NOTE") {
        noteBlockDialog(this, e.target);
      }
      if (e.target.nodeName === "APPCARD") {
        appCardDialog(this, e.target);
      }
    });
  };

  const renderEditorPanel = () => {
    // Empty state
    if (!selectedCard && (!selectedTopic || !selectedTopic.id)) {
      return (
        <div className="editor-empty-state">
          <i className="pi pi-file-edit" />
          <p>Select a topic from the sidebar to begin editing.</p>
        </div>
      );
    }

    // Topic selected (no card): show topic edit form
    if (!selectedCard && selectedTopic && selectedTopic.id) {
      return (
        <div className="topic-edit-form">
          <h5 className="topic-edit-title">Edit Topic</h5>
          <Form>
            <Form.Group controlId="topicName" className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={selectedTopic.name || ''}
                onChange={e => handleTopicFieldChange('name', e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="topicType" className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Control
                as="select"
                value={selectedTopic.type || ''}
                onChange={e => handleTopicFieldChange('type', e.target.value)}
              >
                <option value="paid">Paid</option>
                <option value="free">Free</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="topicPosition" className="mb-3">
              <Form.Label>Position</Form.Label>
              <Form.Control
                type="number"
                value={selectedTopic.position_id ?? ''}
                onChange={e => handleTopicFieldChange('position_id', e.target.value ? parseInt(e.target.value) : null)}
              />
            </Form.Group>
            <Button className="ne-btn ne-btn-primary" onClick={updateTopic}>Save Topic</Button>
          </Form>
        </div>
      );
    }

    // Card selected: show TinyMCE editor
    return (
      <div className="mark-input" style={{ position: 'relative' }}>
        <Editor
          value={content}
          init={{
            menubar: 'file edit insert view tools ',
            menu: {
              custom: { title: 'Media', items: 'link myCustomMenuItem' }
            },
            skin: false,
            init_instance_callback: init_instance,
            setup: function (editor) {
              editor.ui.registry.addButton('quiz', {
                text: 'Quiz',
                onAction: function () { quizDialog(editor); }
              });
              editor.ui.registry.addButton('noteCard', {
                text: 'Note',
                onAction: function () { noteBlockDialog(editor); }
              });
              editor.ui.registry.addButton('courseCard', {
                text: 'Course Card',
                onAction: function () { courseCardDialog(editor); }
              });
              editor.ui.registry.addButton('appCard', {
                text: 'App Card',
                onAction: function () { appCardDialog(editor); }
              });
              editor.ui.registry.addMenuItem('quiz', {
                text: 'Quiz',
                onAction: function () { quizDialog(editor); }
              });
              return {
                getMetadata: function () {
                  return { name: 'Nerdish plugin', url: 'http://nerdish.io' };
                }
              };
            },
            height: 500,
            plugins: [
              'image', 'link', 'lists', 'paste',
              'visualblocks', 'wordcount', 'code',
              'nonbreaking', 'charmap'
            ],
            toolbar:
              'undo redo | styleselect | quiz noteCard courseCard appCard | bold italic | h1 h2 h3 | bullist numlist | superscript subscript | nonbreaking charmap | image |  code | help',
            image_caption: true,
            // TinyMCE turns the browser's spellcheck off unless asked; the
            // browser's own right-click menu offers the suggestions.
            browser_spellcheck: true,
            contextmenu: false,
            font_formats: "Quicksand=quicksand",
            content_style:
              "@import url('https://fonts.googleapis.com/css2?family=Noto+Sans&display=swap'); body { font-family: Noto Sans; font-size: 14pt;}" + MARK_STYLES,
            style_formats: [
              {
                title: 'Blocks', items: [
                  { title: 'text', block: 'p', wrapper: false, merge_siblings: false },
                ]
              }
            ],
            visualblocks_default_state: true,
            end_container_on_empty_block: true,
            // The mobile reader only renders text inside <p> (see
            // backend app/services/html_normalizer.py). Typed text must become <p>, and
            // divs are excluded so legacy div-wrapped content is unwrapped
            // on load instead of hiding text from mobile.
            forced_root_block: 'p',
            valid_elements: 'p,h1,h2,h3,img[src|alt],figure,figcaption,ol,ul,li[index],strong,em,a[href],sup,sub',
            valid_children: '+p[strong|em|a|#text|sup|sub],-p[img|note|course|quiz|appcard],-appcard[appcard],+ul[li],+ol[li],-strong[img],a[#text]',
            extended_valid_elements: "quiz[title|question|answer1|answer2|answer3|correct_answer],note[img|title],course[id|title|actiontext|formaction|postactiontext],button[type|formaction|#text],appcard[app|ios|android|name|tagline|rating|img|text]",
            custom_elements: "~quiz,~note,~course,appcard",
            content_css: "editor.css",
            entity_encoding: 'raw'
          }}
          onEditorChange={handleEditorChange}
        />
        <ImageUploader uploadFolder={selectedCourse.id} />
        <FactsPanel card={selectedCard} token={user.token} onFactsChange={handleFactsChange} />
      </div>
    );
  };

  return (
    <div className="ne-app">
      <ContentContext.Provider value={{ selectedCourse, handleCourseChange }}>
        <Header />
        <Toast ref={toast} />

        {/* Top bar */}
        <div className="ne-topbar">
          <div className="ne-topbar-left">
            <SelectCourse searchQuery={location.search} ref={selectCourseRef} />
            <button
              className="ne-btn ne-btn-ghost"
              disabled={!selectedCourse.id}
              onClick={() => {
                // trigger EditCourse modal - use a ref approach
                document.getElementById('ne-edit-course-trigger')?.click();
              }}
            >
              <i className="pi pi-cog" /> Course settings
            </button>
          </div>
          <div className="ne-topbar-right">
            <button
              className="ne-btn ne-btn-ghost"
              disabled={!selectedCourse.id}
              onClick={() => setCourseCheckVisible(true)}
              title="Proofread (or fully review) every card of this course"
            >
              <i className="pi pi-check-square" /> Proofread course
            </button>
            <button
              className="ne-btn ne-btn-ghost"
              disabled={!selectedCourse.id}
              onClick={() => setAudioManagerVisible(true)}
            >
              <i className="pi pi-volume-up" /> Audio
            </button>
            <button
              className="ne-btn ne-btn-ghost"
              disabled={!selectedCourse.id}
              onClick={() => setDurationModalVisible(true)}
              title="Set reading time for all topics in this course"
            >
              <i className="pi pi-clock" /> Reading time
            </button>
            <NewCourse onCreated={() => { if (selectCourseRef.current) selectCourseRef.current.refresh(); }} />
          </div>
        </div>

        {/* Main layout */}
        <div className="ne-layout">
          {/* Sidebar */}
          <div className="ne-sidebar" style={{ width: sidebarWidth, minWidth: 220, maxWidth: 600, flexShrink: 0 }}>
            <CourseTree
              selectedCourse={selectedCourse}
              selectedTopic={selectedTopic}
              selectedCard={selectedCard}
              onTopicSelect={handleTopicChange}
              onCardSelect={handleCardChange}
              openFindings={openFindings}
            />
          </div>
          <div className="ne-resize-handle" onMouseDown={handleMouseDown} />

          {/* Editor area */}
          <div className="ne-editor-area">
            {/* Editor header: breadcrumb + actions */}
            <div className="ne-editor-header">
              <div className="ne-breadcrumb">
                {breadcrumbParts.map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="ne-breadcrumb-sep">/</span>}
                    <span
                      className={`ne-breadcrumb-item ${i === breadcrumbParts.length - 1 ? 'ne-breadcrumb-current' : ''}`}
                      onClick={async () => {
                        if (i === breadcrumbParts.length - 1) return;
                        if (!(await guardNavigation())) return;
                        if (i === 0) { setSelectedCard(null); setSelectedTopic({}); }
                        else if (i === 1 && selectedCard) { setSelectedCard(null); }
                      }}
                      style={i < breadcrumbParts.length - 1 ? { cursor: 'pointer' } : {}}
                    >
                      {part}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              {selectedCard && (
                <span className="ne-position-input">
                  <label>Pos</label>
                  <InputNumber
                    value={handlePositionDisplay()}
                    onChange={(e) => {
                      positionRef.current = e.value;
                      setSelectedCard(prev => ({ ...prev, position_id: e.value }));
                      markDirty();
                    }}
                    style={{ width: '3rem' }}
                  />
                </span>
              )}
              <div className="ne-editor-actions">
                <button
                  className={`ne-btn ${rightPanel === 'preview' ? 'ne-btn-active' : ''}`}
                  onClick={() => setRightPanel(panel => (panel === 'preview' ? 'review' : 'preview'))}
                  title="Switch the right panel between the card preview and the review"
                >
                  <i className="pi pi-eye" /> Preview
                </button>
                <div className="ne-panel-toggle">
                  <span className="ne-check-label"><i className="pi pi-check-square" />Check</span>
                  {CHECK_MODES.map(({ mode, label, title }) => (
                    <button
                      key={mode}
                      className={`ne-panel-toggle-btn ${checkStatus.mode === mode ? 'ne-panel-toggle-active' : ''}`}
                      onClick={() => handleCheck(mode)}
                      disabled={!selectedCard}
                      title={!selectedCard ? 'Select a card to check' : checkStatus.running ? 'A check is running' : title}
                    >
                      {checkStatus.running && checkStatus.mode === mode && (
                        <i className="pi pi-spin pi-spinner ne-check-spinner" />
                      )}
                      {label}
                    </button>
                  ))}
                </div>
                {selectedCard && (
                  <>
                    <span
                      className="ne-save-status"
                      style={{
                        fontSize: '0.85rem',
                        marginRight: '0.5rem',
                        color: saveStatus === 'error' ? '#c0392b' : '#888',
                        minWidth: '5.5rem',
                        textAlign: 'right',
                      }}
                    >
                      {saveStatus === 'saving' && 'Saving…'}
                      {saveStatus === 'saved' && 'Saved'}
                      {saveStatus === 'dirty' && 'Unsaved changes'}
                      {saveStatus === 'error' && 'Save failed'}
                    </span>
                    <button className="ne-btn ne-btn-primary" onClick={handlePublishClick}>
                      <i className="pi pi-check" /> Save
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Editor content */}
            <div className="ne-editor-content" style={{ position: 'relative' }}>
              {renderEditorPanel()}
            </div>

            <div className="ne-linked-courses">
              <LinkedCourses courseId={selectedCourse.id} />
            </div>
          </div>

          {/* Right panel — always rendered, toggled via display:none to preserve state */}
          <div className="ne-preview-panel" style={{ display: rightPanel === 'preview' ? undefined : 'none' }}>
            <CardPreview
              content={content}
              selectedCourse={selectedCourse}
              selectedTopic={selectedTopic}
            />
          </div>
          <div style={{ display: rightPanel === 'review' ? undefined : 'none' }}>
            <ContentReviewPanel
              ref={reviewPanelRef}
              cardId={selectedCard?.id}
              token={user.token}
              bodyHtml={content}
              visible={rightPanel === 'review'}
              progressHost={getProgressHost}
              onStatusChange={handleReviewStatus}
              onFactsChange={handleFactsChange}
              onShowFinding={handleShowFinding}
              onApplyFix={handleApplyFix}
              onApplyFixes={handleApplyFixes}
              onOpenCount={handleOpenCount}
              nextCard={nextCard}
              otherCards={otherCardsWithFindings(openFindings, selectedCard?.id)}
              onNextCard={card => openCheckedCard(card.card_id)}
              beforeRun={flushSave}
            />
          </div>
        </div>

        {/* Hidden EditCourse trigger */}
        <EditCourse selectedCourse={selectedCourse} triggerId="ne-edit-course-trigger" onDeleted={() => {
          setSelectedCourse({});
          setSelectedTopic({});
          setSelectedCard(null);
          setContent('');
          if (selectCourseRef.current) selectCourseRef.current.refresh();
        }} />

        <TopicAudioManager
          visible={isAudioManagerVisible}
          onHide={() => setAudioManagerVisible(false)}
          course={selectedCourse}
          token={user ? user.token : ""}
          toastRef={toast}
        />

        <CourseReviewModal
          visible={courseCheckVisible}
          course={selectedCourse.id ? { id: selectedCourse.id, name: selectedCourse.name } : null}
          token={user.token}
          beforeRun={flushSave}
          onClose={closeCourseCheck}
          onOpenCard={(cardId) => { closeCourseCheck(); openCheckedCard(cardId); }}
        />

        <CourseDurationModal
          visible={isDurationModalVisible}
          course={selectedCourse.id ? selectedCourse : null}
          token={user ? user.token : ""}
          onClose={() => setDurationModalVisible(false)}
        />
      </ContentContext.Provider>
    </div>
  )
}
